/**
 * `CohortSection` — wraps the cohort retention heatmap with a section
 * header, an inline country filter, and loading / error / empty
 * states. The heatmap itself is `CohortHeatmap`; this file owns the
 * section framing + filter chrome.
 *
 * The country filter posts to `/internal/admin/metrics/cohorts?country=<…>`
 * via the parent (`MetricsView` owns the fetch lifecycle). This
 * section is presentational only — it surfaces the resolved
 * `cohorts.country` so the staff member can confirm what segment
 * they're looking at, and renders the per-segment grid.
 */

import type { AdminMetricsCohorts, AdminMetricsGeographyRow } from '@sill/types';

import { Card } from '../../../components/atoms/Card.js';
import { countryName } from '../../../lib/country-names.js';
import { useC } from '../../../lib/theme.js';
import { CohortHeatmap } from '../CohortHeatmap.js';
import { CountryFilter } from '../CountryFilter.js';
import type { CountryFilterValue } from '../CountryFilter.js';
import { SectionHeader } from '../KpiTile.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export type CohortSectionState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; cohorts: AdminMetricsCohorts };

export interface CohortSectionProps {
  state: CohortSectionState;
  /** Country options for the filter dropdown — taken from the overview
   *  `geography` array (already top-N + sorted DESC by active_accounts). */
  countries: ReadonlyArray<AdminMetricsGeographyRow>;
  /** The country segment the user has selected. Default `'all'`. */
  country: CountryFilterValue;
  /** Fired when the staff member changes the segment. Parent re-fetches. */
  onCountryChange: (next: CountryFilterValue) => void;
}

export function CohortSection({
  state,
  countries,
  country,
  onCountryChange,
}: CohortSectionProps): JSX.Element {
  const C = useC();

  // Resolved-server segment for the chip — prefer the value the
  // server echoed (handles the case where the client requested e.g.
  // `'us'` lowercase and the server normalised to `'US'`). Falls
  // back to the client-requested value when not ready.
  const resolved =
    state.kind === 'ready' ? state.cohorts.country : country;
  const segmentLabel = segmentDisplayLabel(resolved);

  return (
    <section>
      <div
        className="flex flex-wrap items-end justify-between gap-3 mb-4"
        style={{ rowGap: 12 }}
      >
        <div>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 2,
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            Retention
          </div>
          <h2
            style={{
              fontFamily: "'Instrument Serif', serif",
              fontSize: 24,
              color: C.text,
              letterSpacing: -0.8,
              lineHeight: 1.1,
              margin: 0,
              display: 'flex',
              alignItems: 'baseline',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            <span>Cohort retention</span>
            <SegmentChip label={segmentLabel} />
          </h2>
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.muted,
              marginTop: 4,
              lineHeight: 1.5,
              maxWidth: 640,
            }}
          >
            Rows = signup month. Columns = months since signup
            (M0 = signup month itself). Cell colour = retention percentage.
            Change the country segment to slice by an account&rsquo;s modal country.
          </div>
        </div>
        <CountryFilter
          value={country}
          countries={countries}
          onChange={onCountryChange}
          disabled={state.kind === 'loading'}
        />
      </div>

      {state.kind === 'loading' ? (
        <Card style={{ padding: 24 }}>
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.dim,
              letterSpacing: 1.5,
            }}
          >
            Loading cohorts…
          </span>
        </Card>
      ) : state.kind === 'error' ? (
        <Card style={{ padding: 18 }}>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.coralFg,
              letterSpacing: 2,
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            Cohorts unavailable
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.body }}>
            {state.message}
          </div>
        </Card>
      ) : state.cohorts.cohorts.length === 0 ? (
        <Card style={{ padding: 24 }}>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 2,
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            No cohorts in segment
          </div>
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.body,
              lineHeight: 1.5,
            }}
          >
            {resolved === 'all'
              ? 'No cohorts yet. Retention data accrues once accounts complete a full month.'
              : `No accounts have ${segmentLabel} as their modal country yet.`}
          </div>
        </Card>
      ) : (
        <CohortHeatmap cohorts={state.cohorts.cohorts} />
      )}
    </section>
  );
}

/**
 * Human label for the resolved segment chip. Matches `CountryFilter`'s
 * options so the chip always reads the same as the dropdown option.
 */
function segmentDisplayLabel(segment: string): string {
  if (segment === 'all') return 'All countries';
  return countryName(segment);
}

function SegmentChip({ label }: { label: string }): JSX.Element {
  const C = useC();
  return (
    <span
      className="inline-flex items-center px-2 py-0.5"
      style={{
        fontFamily: monoFont,
        fontSize: 10,
        letterSpacing: 1.2,
        textTransform: 'uppercase',
        color: C.limeFg,
        background: C.limeDim,
        border: `1px solid rgba(217,255,63,0.3)`,
        borderRadius: 2,
        fontWeight: 500,
      }}
    >
      {label}
    </span>
  );
}
